import Address from '@/gf/components/Address'
import AddressModalForm from '@/gf/components/AddressModalForm'
import Button from '@/gf/components/ButtonOld'
import Card from '@/gf/components/Card'
import Field from '@/gf/components/Field'
import ItemQuantitiesInput from '@/gf/components/ItemQuantitiesInput'
import Layout from '@/gf/components/Layout'
import Select from '@/gf/components/Select'
import TextField from '@/gf/components/TextField'
import TextInput from '@/gf/components/TextInput'
import useMsgs from '@/gf/hooks/useMsgs'
import useToggle from '@/gf/hooks/useToggle'
import Phone from '@/gf/modules/Phone'
import StoreOrderM from '@/gf/modules/StoreOrder'
import { useState } from 'react'
import {
  Address as AddressT,
  Dimensions,
  DimensionsUnit,
  GroundShipmentPayload,
  Option,
  ShipmentItemInput,
  ShippingLabelSize,
  Weight,
  WeightUnit,
} from '../../types'
import AddressMetadata from '../components/AddressMetadata'
import CustomerInfoModalForm from '../components/CustomerInfoModalForm'
import { StoreOrdersSearch } from '../modules/api'

interface WeightUnitOption extends Option {
  id: WeightUnit
}
const weightUnitOptions: WeightUnitOption[] = [
  { id: 'gram', display: 'g' },
  { id: 'kilogram', display: 'kg' },
  { id: 'pound', display: 'lb' },
  { id: 'ounce', display: 'oz' },
]
interface DimensionsUnitOption extends Option {
  id: DimensionsUnit
}
const dimensionsUnitOptions: DimensionsUnitOption[] = [
  { id: 'inch', display: 'in' },
  { id: 'centimeter', display: 'cm' },
]
interface ShippingLabelSizeOption extends Option {
  id: ShippingLabelSize
}
const labelSizeOptions: ShippingLabelSizeOption[] = [
  { id: '4x6', display: '4 inches by 6 inches' },
  { id: 'letter', display: 'U.S. Letter size (8.5 inches by 11 inches)' },
]

const ConfigureShipmentForm = ({
  storeOrder,
  spinnerLive,
  onSubmitGround,
  onSubmitLtl,
}: {
  storeOrder: StoreOrdersSearch['storeOrders'][number]
  spinnerLive: boolean
  onSubmitGround: (groundShipmentPayload: GroundShipmentPayload) => void
  onSubmitLtl: () => void
}) => {
  const [_msgs, msgsMgr] = useMsgs()
  const [shippingType, setShippingType] = useState('ground')
  const [shipToAddress, setShipToAddress] = useState<AddressT | null>(storeOrder.shippingAddress)
  const [shipToAddressModalOpen, shipToAddressModalToggle] = useToggle()
  const [shipToPhone, setShipToPhone] = useState<string>(Phone.format(storeOrder.order.phoneNumber))
  const [shipToPhoneModalOpen, shipToPhoneModalToggle] = useToggle()
  const [shipFromAddress, setShipFromAddress] = useState<AddressT | undefined>(
    storeOrder.store.address
  )
  const [shipFromAddressModalOpen, shipFromAddressModalToggle] = useToggle()
  const [shipFromPhone, setShipFromPhone] = useState<string | undefined>(
    storeOrder.store.phoneNumber ? Phone.format(storeOrder.store.phoneNumber) : undefined
  )
  const [shipFromPhoneModalOpen, shipFromPhoneModalToggle] = useToggle()
  const [labelWeight, setLabelWeight] = useState('')
  const [labelWeightUnits, setLabelWeightUnits] = useState<WeightUnit>('pound')
  const [labelDimensionsLength, setLabelDimensionsLength] = useState('')
  const [labelDimensionsWidth, setLabelDimensionsWidth] = useState('')
  const [labelDimensionsHeight, setLabelDimensionsHeight] = useState('')
  const [labelDimensionsUnits, setLabelDimensionsUnits] = useState<DimensionsUnit>('inch')
  const [labelSize, setLabelSize] = useState<ShippingLabelSize>('4x6')
  const [shipDate, setShipDate] = useState<string>(new Date().toISOString().split('T')[0])

  const unshippedLineItems = StoreOrderM.getUnshipped(storeOrder.lineItems)
  const [lineItemQuantities, setLineItemQuantities] = useState<Record<string, string>>(
    unshippedLineItems.reduce((acc, lineItem) => ({ ...acc, [lineItem.id]: lineItem.quantity }), {})
  )
  const shipmentItemsCount = Object.values(lineItemQuantities).reduce(
    (acc, quantity) => acc + (parseFloat(quantity) || 0),
    0
  )
  const possibleShipmentItemsCount = unshippedLineItems.reduce(
    (acc, lineItem) => acc + lineItem.quantity,
    0
  )

  const handleGroundShipmentSubmit = () => {
    // Validate form
    if (!shipToAddress) {
      msgsMgr.add('Ship to address is required.', 'negative')
      return
    }
    if (!shipFromAddress) {
      msgsMgr.add('Ship from address is required.', 'negative')
      return
    }
    if (!shipFromPhone) {
      msgsMgr.add('Ship from phone number is required.', 'negative')
      return
    }
    const weight: Weight = {
      unit: labelWeightUnits,
      value: parseFloat(labelWeight),
    }
    const dimensions: Dimensions = {
      unit: labelDimensionsUnits,
      length: parseFloat(labelDimensionsLength),
      width: parseFloat(labelDimensionsWidth),
      height: parseFloat(labelDimensionsHeight),
    }
    const shipmentItems: ShipmentItemInput[] = Object.entries(lineItemQuantities)
      .map(([lineItemId, quantity]) => ({
        lineItemId,
        quantity: parseFloat(quantity) || 0,
      }))
      .filter((shipmentInput) => shipmentInput.quantity !== 0)
    onSubmitGround({
      storeOrderId: storeOrder.id,
      shipToAddress,
      shipToPhone,
      shipFromAddress,
      shipFromPhone,
      weight,
      dimensions,
      labelSize,
      shipmentItems,
      shipDate,
    })
  }

  const handleLtlShipmentSubmit = () => {
    // TODO: LTL stubbed for future implementation
    onSubmitLtl()
  }

  return (
    <>
      {/* Edit ship to address modal */}
      <AddressModalForm
        title="Edit ship to address"
        open={shipToAddressModalOpen}
        onClose={shipToAddressModalToggle.off}
        initialAddress={shipToAddress}
        onSubmit={(newAddress) => {
          setShipToAddress(newAddress)
          shipToAddressModalToggle.off()
        }}
      />
      {/* Edit ship from address modal */}
      <AddressModalForm
        title="Edit ship from address"
        open={shipFromAddressModalOpen}
        onClose={shipFromAddressModalToggle.off}
        initialAddress={shipFromAddress}
        onSubmit={(newAddress) => {
          setShipFromAddress(newAddress)
          shipFromAddressModalToggle.off()
        }}
      />
      {/* Edit ship to phone modal */}
      <CustomerInfoModalForm
        title="Edit ship to phone number"
        initialCustomerInfo={{
          email: '',
          phoneNumber: shipToPhone,
        }}
        open={shipToPhoneModalOpen}
        onClose={shipToPhoneModalToggle.off}
        onSubmit={({ phoneNumber }) => {
          setShipToPhone(phoneNumber)
          shipToPhoneModalToggle.off()
        }}
        showEmail={false}
      />
      {/* Edit ship from phone modal */}
      <CustomerInfoModalForm
        title="Edit ship from phone number"
        initialCustomerInfo={{
          email: '',
          phoneNumber: shipFromPhone || '',
        }}
        open={shipFromPhoneModalOpen}
        onClose={shipFromPhoneModalToggle.off}
        onSubmit={({ phoneNumber }) => {
          setShipFromPhone(phoneNumber)
          shipFromPhoneModalToggle.off()
        }}
        showEmail={false}
      />
      <form
        onSubmit={(e) => {
          e.preventDefault()
          if (shipmentItemsCount === 0) {
            msgsMgr.add('Cannot create a shipment with no items.', 'negative')
            return
          }
          if (shippingType === 'ground') {
            handleGroundShipmentSubmit()
          } else {
            handleLtlShipmentSubmit()
          }
        }}
      >
        <Layout>
          <Layout.Section type="primary">
            <Card title={`Order #${storeOrder.shortId}`}>
              <ItemQuantitiesInput
                items={unshippedLineItems}
                itemQuantities={lineItemQuantities}
                onChangeItemQuantities={setLineItemQuantities}
              />
              <Card.Section title="Shipment Details">
                <Card.SubSection key="shipment-type">
                  <div className="space-y-2">
                    <Select
                      className="w-full"
                      currentId={shippingType}
                      options={[
                        { id: 'ground', display: 'Ground' },
                        { id: 'ltl', display: 'LTL' },
                      ]}
                      onChange={(shippingTypeId) => setShippingType(shippingTypeId)}
                    />
                    <Field label="Ship date">
                      <input
                        required
                        className="focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                        type="date"
                        value={shipDate}
                        onChange={(e) => setShipDate(e.target.value)}
                        min={new Date().toISOString().split('T')[0]}
                      />
                    </Field>
                  </div>
                </Card.SubSection>
                <Card.SubSection key="shipment-details">
                  {shippingType === 'ltl' ? (
                    <>
                      <p className="text-sm text-gray-700">
                        Only ground shipping is available at this time. We are working on supporting
                        LTL shipping in the future.
                      </p>
                    </>
                  ) : (
                    <div className="space-y-2">
                      <Field label="Total weight (with package)">
                        <div className="grid grid-cols-4 gap-x-2">
                          <TextInput
                            required
                            type="number"
                            min={0}
                            value={labelWeight}
                            onChange={(e) => setLabelWeight(e.target.value)}
                          />
                          <Select
                            required
                            options={weightUnitOptions}
                            currentId={labelWeightUnits}
                            onChange={(weightUnit) => setLabelWeightUnits(weightUnit as WeightUnit)}
                          />
                        </div>
                      </Field>
                      <div>
                        <p className="text-sm text-gray-700">Package dimensions</p>
                        <div className="grid grid-cols-4 gap-x-2">
                          <TextField
                            required
                            label="Length"
                            type="number"
                            min={0}
                            value={labelDimensionsLength}
                            onChange={(e) => setLabelDimensionsLength(e.target.value)}
                          />
                          <TextField
                            required
                            label="Width"
                            type="number"
                            min={0}
                            value={labelDimensionsWidth}
                            onChange={(e) => setLabelDimensionsWidth(e.target.value)}
                          />
                          <TextField
                            required
                            label="Height"
                            type="number"
                            min={0}
                            value={labelDimensionsHeight}
                            onChange={(e) => setLabelDimensionsHeight(e.target.value)}
                          />
                          <Field label="unit">
                            <Select
                              className="w-full"
                              required
                              options={dimensionsUnitOptions}
                              currentId={labelDimensionsUnits}
                              onChange={(dimensionsUnit) =>
                                setLabelDimensionsUnits(dimensionsUnit as DimensionsUnit)
                              }
                            />
                          </Field>
                        </div>
                      </div>
                    </div>
                  )}
                </Card.SubSection>
                {shippingType === 'ground' && (
                  <Card.SubSection>
                    <Field label="Shipping label dimensions">
                      <Select
                        className="w-full"
                        required
                        currentId={labelSize}
                        options={labelSizeOptions}
                        onChange={(labelSizeId) => setLabelSize(labelSizeId as ShippingLabelSize)}
                      />
                    </Field>
                  </Card.SubSection>
                )}
              </Card.Section>
            </Card>
          </Layout.Section>
          <Layout.Section type="secondary">
            <Card title="Ship To">
              <Card.Section
                title="Phone"
                headerButton={
                  <button
                    className="text-sm text-blue-500 underline cursor-pointer"
                    type="button"
                    onClick={() => shipToPhoneModalToggle.on()}
                  >
                    Edit
                  </button>
                }
              >
                <p className="text-sm text-gray-700">{shipToPhone}</p>
              </Card.Section>
              <Card.Section
                title="Address"
                headerButton={
                  <button
                    className="text-sm text-blue-500 underline cursor-pointer"
                    type="button"
                    onClick={() => shipToAddressModalToggle.on()}
                  >
                    Edit
                  </button>
                }
              >
                {shipToAddress ? (
                  shipToAddress === storeOrder.shippingAddress ? (
                    <>
                      <Address
                        className="text-sm text-gray-700"
                        address={storeOrder.shippingAddress}
                      />
                      {typeof storeOrder.shippingAddress.deliverable === 'boolean' && (
                        <AddressMetadata className="mt-2" address={storeOrder.shippingAddress} />
                      )}
                    </>
                  ) : (
                    <Address className="text-sm text-gray-700" address={shipToAddress} />
                  )
                ) : (
                  <p className="text-sm text-gray-700">Set address to ship to.</p>
                )}
              </Card.Section>
            </Card>
            <Card title="Ship From">
              <Card.Section
                title="Phone"
                headerButton={
                  <button
                    className="text-sm text-blue-500 underline cursor-pointer"
                    type="button"
                    onClick={() => shipFromPhoneModalToggle.on()}
                  >
                    Edit
                  </button>
                }
              >
                <p className="text-sm text-gray-700">{shipFromPhone || 'Set phone number.'}</p>
              </Card.Section>
              <Card.Section
                title="Address"
                headerButton={
                  <button
                    className="text-sm text-blue-500 underline cursor-pointer"
                    type="button"
                    onClick={() => shipFromAddressModalToggle.on()}
                  >
                    Edit
                  </button>
                }
              >
                {shipFromAddress ? (
                  <Address className="text-sm text-gray-700" address={shipFromAddress} />
                ) : (
                  <p className="text-sm text-gray-700">Set address to ship from.</p>
                )}
              </Card.Section>
            </Card>
            <Card title="Summary">
              <Card.Section>
                <p className="text-sm text-gray-700">
                  Creating shipment containing {shipmentItemsCount} of {possibleShipmentItemsCount}{' '}
                  items.
                </p>
              </Card.Section>
              <Card.Section>
                <Button
                  className="w-full justify-center"
                  title="Submit"
                  primary
                  type="submit"
                  performing={spinnerLive}
                />
                {spinnerLive && (
                  <div className="mt-1 text-sm text-gray-500">
                    Generating label - this can take up to 45 seconds.
                  </div>
                )}
              </Card.Section>
            </Card>
          </Layout.Section>
        </Layout>
      </form>
    </>
  )
}

export default ConfigureShipmentForm
